import React, {useRef} from 'react';
import { Toast } from 'primereact/toast';
import type { MenuProps } from 'antd';
import { Menu, Badge } from 'antd';
import {useLocation, useNavigate} from 'react-router-dom';
import {useCollection} from 'react-firebase-hooks/firestore';
import {collection, getFirestore, query, where} from 'firebase/firestore';
import Firebase from '../Firebase/Firebase';

const db = getFirestore(Firebase);

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: 'group',
): MenuItem {
    return {
        key,
        icon,
        children,
        label,
        type,
    } as MenuItem;
}

type Props = {
    collapsed: boolean;
    onNavigate?: () => void;
};

const baseItems: MenuProps['items'] = [
    getItem('Home', 'home', <i  className="pi pi-home" style={{'fontSize': '1.4em'}}/>),
    getItem('Buyers', 'buyers', <i  className="pi pi-users" style={{'fontSize': '1.4em'}}/>),
    getItem('Products', 'products', <i  className="pi pi-shopping-bag" style={{'fontSize': '1.4em'}}/>),
    getItem('Shops', 'shops', <i  className="pi pi-building" style={{'fontSize': '1.4em'}}/>),
    getItem('Transactions', 'transactions', <i  className="pi pi-money-bill" style={{'fontSize': '1.4em'}}/>),
    getItem('Support', 'support', <i  className="pi pi-comments" style={{'fontSize': '1.4em'}}/>),
    getItem('Disputes', 'disputes', <i  className="pi pi-exclamation-triangle" style={{'fontSize': '1.4em'}}/>),
    getItem('Appeals', 'appeals', <i  className="pi pi-flag" style={{'fontSize': '1.4em'}}/>),
    getItem('Referral Codes', 'referral-codes', <i  className="pi pi-share-alt" style={{'fontSize': '1.4em'}}/>),
    getItem('Discount Codes', 'discount-codes', <i  className="pi pi-percentage" style={{'fontSize': '1.4em'}}/>),
    getItem('Preorder Extensions', 'preorder-extensions', <i  className="pi pi-clock" style={{'fontSize': '1.4em'}}/>),
    getItem('Promotions', 'promotions', <i  className="pi pi-tag" style={{'fontSize': '1.4em'}}/>),
    getItem('Catalog',   'catalog',   <i className="pi pi-sitemap"      style={{'fontSize': '1.4em'}}/>),
    getItem('Analytics', 'analytics', <i className="pi pi-chart-bar"    style={{'fontSize': '1.4em'}}/>),
    getItem('Logs',      'logs',      <i className="pi pi-list"          style={{'fontSize': '1.4em'}}/>),
    getItem('Settings',  'settings',  <i className="pi pi-cog"          style={{'fontSize': '1.4em'}}/>),
    getItem('Profile',   'profile',   <i className="pi pi-user"         style={{'fontSize': '1.4em'}}/>),
];

const MenuOptions: React.FC<Props> = ({onNavigate}) => {
    const toast = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    const [pendingAppeals] = useCollection(
        query(collection(db, 'productAppeals'), where('status', '==', 'pending')),
    );
    const pendingAppealCount = pendingAppeals?.docs.length ?? 0;

    // Open contact-form requests (chats and tickets join this count later).
    const [openSupport] = useCollection(
        query(collection(db, 'supportRequests'), where('status', '==', 'open')),
    );
    // Unread support chats (users/shopped-support/messages with isRead false).
    const [unreadChats] = useCollection(
        query(collection(db, 'users', 'shopped-support', 'messages'), where('isRead', '==', false)),
    );
    // New tickets / suggestions nobody has picked up yet.
    const [newTickets] = useCollection(
        query(collection(db, 'supportTickets'), where('status', 'in', ['open', 'received'])),
    );
    const openSupportCount = (openSupport?.docs.length ?? 0) + (unreadChats?.docs.length ?? 0) + (newTickets?.docs.length ?? 0);

    const items: MenuProps['items'] = baseItems.map((item) => {
        if (item && 'key' in item && item.key === 'support' && openSupportCount > 0) {
            return {
                ...item,
                label: <Badge count={openSupportCount} size="small" offset={[10, 0]}>Support</Badge>,
            };
        }
        if (item && 'key' in item && item.key === 'appeals' && pendingAppealCount > 0) {
            return {
                ...item,
                label: <Badge count={pendingAppealCount} size="small" offset={[10, 0]}>Appeals</Badge>,
            };
        }
        return item;
    });

    const gotoPath = (path: string) => {
        navigate(`/${path}`);
    };

    const onClick: MenuProps['onClick'] = e => {
        gotoPath(e.key);
        onNavigate?.();
    };

    const getKey = (path: string) => {
        var exactPath = path.split('/')[1];

        if(items.some(x => x?.key === exactPath)){
            return exactPath
        }

        return exactPath;
    }

    // @ts-ignore
    return (
        <div>
            <Toast ref={toast}></Toast>
            <Menu
                onClick={onClick}
                style={{borderRight:'none'}}
                selectedKeys={[getKey(location.pathname)]}
                items={items}
            />
        </div>
    );
}

export default MenuOptions;
